import { calendarToday, coachZone, dayKeyOf } from "../lib/days";
import { ReviewingCard } from "../components/ReviewingCard";
import { useEffect, useRef, useState } from "react";
import { Workspace } from "../components/Shell";
import { CoachSetupPrompt } from "../components/CoachSetupPrompt";
import { href, routeQuery } from "../lib/route";
import { useQuerySort } from "../lib/useQuerySort";
import { SeeExampleLink } from "../components/ExampleBanner";
import {
  TODO_EMPTY, criticalWords, isLongWait, queueShowsAll, rowTitle, showAllLabel, todoList, visibleWaiting,
  waitDays, waitShort,
} from "../lib/reviewQueue";
import { useProposals, useRosterData } from "./useRosterData";
import { useReviewQueue } from "./useReviewQueue";
import { useReminders } from "./useReminders";
import { RemindersCard } from "../components/Reminders";
import { ReminderWindow, type ReminderTarget } from "../components/ReminderWindow";
import { useOpenerFocus } from "../components/useOpenerFocus";
import { useCalls } from "./useCalls";
import { CallWindow, CallsCard, type CallTarget } from "../components/Calls";
import { CallHost } from "../components/CallScreen";
import { CALLS_INCLUDED } from "../lib/calls";

/* DASHBOARD — ONE `To do` LIST, AND NOTHING THE COACH HAS TO READ PAST (W101, his word 2026-09-30: *"Only the name of the
 * client and name of workout and how many days waiting is more than enough … As clean as possible! And only the mandatory
 * info!! Minimal!"*; W111 the same evening: *"what purpose does "needs you" have?"*). The page, top to bottom: the title ALONE
 * (job 3 step 5 page 7, 2026-10-09: W101's summary line `1 critical · 3 to review` said the rows' count above the rows and the
 * sidebar badge's beside it — the rows are the count, `Show all N ›` the total past eight); the `To do` card — CRITICAL clients first (training runs out
 * now: the name, `Overdue 3 days` / `Last day tomorrow`, `Assign`), then the workouts to review and the replies waiting TOGETHER,
 * oldest wait first, each the client, the workout and the wait; beside it (W117, his word 2026-10-01: *"move the reminders
 * container top right"*) the coach's REMINDERS, each the text and its day — two columns above 60rem, one below. The doors
 * (All clients, Library) are GONE (W117: the sidebar holds both), and so is the page's own search (the Shell's top bar
 * draws it, centred, on every page). Everything else — sets done of written, PRs, RPE, skips, notes, clips — is one click
 * in, on the review.
 *
 * ⚠️ THE ROWS ARE NOT CHOSEN HERE. Which workouts wait, which threads wait and their order are `reviewQueue.ts`'s (W82 §8,
 * pinned to the Swift corpus) — W101 changed what is DRAWN, never which rows qualify. THIS FILE DERIVES NOTHING; the reads
 * are `useRosterData` + `useReviewQueue`, the SAME hooks the sidebar's badge reads, and the summary's two halves sum to it.
 *
 * ⚠️ ONE PRIMARY: `Review next ›` (the oldest waiting), on the right of the To do card's head (W117). A queue row's box opens the workout's OVERVIEW (W93, his word) and
 * its `Review` — the one quiet control on the row — goes straight into the review; a reply row IS its link (the thread). */
export function Dashboard({ coachId }: { coachId: string }) {
  const heading = useRef<HTMLHeadingElement>(null);
  useEffect(() => { heading.current?.focus(); }, []);
  // ONE "today" per load, so no two rows can disagree about the day across a midnight.
  const [today] = useState(() => calendarToday());
  const [instant] = useState(() => Date.now());
  // W82 step 6 — the coach's reminders, on the same clock as the queue: one instant, the coach's own zone.
  const [zone] = useState(() => coachZone());
  const [reminderNow] = useState(() => new Date(instant));
  const reminders = useReminders(coachId, zone);
  const [reminderTarget, setReminderTarget] = useState<ReminderTarget | null>(null);
  // Scheduled calls (his 2026-10-10 idea) — the next seven days', a card above the stack; none, no card.
  const calls = useCalls(coachId, zone);
  const [callTarget, setCallTarget] = useState<CallTarget | null>(null);
  /* JOB 3 STEP 5 PAGE 7 — the reminder window is mounted ON DEMAND (page 6's way: it sat in the page as a closed dialog with
     its four fields); focus goes back to what opened it — `+ Reminder` or a row's title — or the heading when that row is gone. */
  const rememberOpener = useOpenerFocus(reminderTarget !== null, heading);
  const editReminder = (t: ReminderTarget) => { rememberOpener(); setReminderTarget(t); };
  const roster = useRosterData(0, today);
  const data = roster.status === "ready" ? roster.data : null;
  const proposals = useProposals(data);
  const state = useReviewQueue(data, proposals, today);
  const q = state.status === "ready" ? state.queue : null;
  // The first eight or all of them — IN THE URL (`#/?queue=all`), read through the hook that listens to `hashchange`: the
  // Dashboard does not remount when only its query changes (the codex's never-remounting screen).
  const [showAll] = useQuerySort<boolean>(queueShowsAll, routeQuery);

  const clients = q === null ? 0 : q.order.length + q.invited.length;
  const next = q?.waiting[0];
  // W111: the ONE list — critical, then reviews and replies together (a reset workout's reply opens the conversation).
  const todo = q === null ? [] : todoList(q);
  const fullName = (clientId: string, first: string) => q?.order.find((o) => o.clientId === clientId)?.name ?? first;
  const failed = roster.status === "error" ? roster.message : state.status === "error" ? state.message : null;

  return (
    <Workspace>
      <main id="main" className="queue-page ours-page">
        <header className="ours-head queue-head">
          <div className="ours-head-text">
            <h1 className="ht-title ours-title" ref={heading} tabIndex={-1}>Dashboard</h1>
          </div>
        </header>

        <CoachSetupPrompt coachId={coachId} />

        {failed === null && q === null && <p className="ht-callout ours-quiet" aria-live="polite">Loading your queue…</p>}
        {failed !== null && (
          <div role="alert" className="ours-error">
            <p className="ht-body">Could not load your clients.</p>
            <p className="ht-caption">{failed}</p>
          </div>
        )}

        {q !== null && clients === 0 && (
          <p className="ht-callout ours-quiet">
            No clients yet. <a className="ht-link" href={href({ name: "roster" })}>Add your first client</a> and their work appears here.<SeeExampleLink />
          </p>
        )}

        <CallsCard state={calls.state} coachId={coachId} now={reminderNow} zone={zone} onOpen={setCallTarget} />

        {q !== null && clients > 0 && (
          <div className="queue-stack">
            {/* 🔴 W111 — ONE `To do` LIST (his word, 2026-09-30: *"what purpose does "needs you" have?"*): CRITICAL first (a
                client whose training runs out now), then the workouts to review and the replies TOGETHER, oldest wait first.
                Which rows and their order are `reviewQueue.todoList` (pinned to the Swift corpus); this file draws them. */}
            {/* W117: the To do card is ALWAYS the stack's first child, so the Reminders card keeps the right column; empty, it says
                its ONE sentence (`TODO_EMPTY`, page 7 — the head no longer says `Nothing waiting`); `Review next ›` — the one primary — sits on the right of its head. */}
            <section className="queue-card" aria-labelledby="todo-title">
                <header className="queue-card-head">
                  <h2 id="todo-title" className="queue-card-title">To do</h2>
                  {next !== undefined && (
                    <a className="ht-button ht-button-primary queue-next" href={href({ name: "review", sessionId: next.sessionId })}>
                      Review next ›
                    </a>
                  )}
                </header>
                {todo.length === 0 ? <p className="queue-empty">{TODO_EMPTY}</p> : <ol className="queue-rows">
                  {visibleWaiting(todo, showAll).map((t) => {
                    if (t.kind === "critical") {
                      // William's first look (10-14) → Fable 10-15: PLAIN words, no `critical`; the whole row is the link to the
                      // client's Week (where the coach fixes it) and `Assign` on the right SHOWS it is one — he clicked the rows
                      // and could not tell they went anywhere (MEASURED 10-15: the link already covered the row; the eye had no word).
                      const words = criticalWords(t.row.lastDay, dayKeyOf(today));
                      return (
                        <li key={`critical-${t.row.clientId}`} className="queue-row is-linked">
                          <a className="queue-open" href={`${href({ name: "client", clientId: t.row.clientId })}?tab=training`}
                             aria-label={`Assign ${t.row.name}'s training — ${words}`} />
                          <span className="queue-title">{t.row.name}</span>
                          <span className="queue-workout">{words}</span>
                          <span className="queue-wait" />
                          <span className="ht-button queue-review queue-go" aria-hidden="true">Assign</span>
                        </li>
                      );
                    }
                    if (t.kind === "review") {
                      const r = t.row;
                      const wait = waitDays(r.finish, instant);
                      // W93 (his word, 2026-09-28): the WHOLE ROW opens the workout's OVERVIEW (written · done) — the
                      // `queue-open` link's box is stretched over the row — and the Review button, drawn above that box,
                      // goes STRAIGHT into the review. W101: the row draws the client, the workout and the wait, nothing else.
                      return (
                        <li key={r.sessionId} className="queue-row is-linked">
                          <a className="queue-open" href={href({ name: "sessionOverview", sessionId: r.sessionId })}
                             aria-label={`Open ${rowTitle(r)} — what was written and done`} />
                          <span className="queue-title">{r.name}</span>
                          <span className="queue-workout">{r.workout ?? ""}</span>
                          <span className={isLongWait(wait) ? "queue-wait is-long" : "queue-wait"}>{waitShort(wait)}</span>
                          <a className="ht-button queue-review is-go" href={href({ name: "review", sessionId: r.sessionId })}
                             aria-label={`Review ${rowTitle(r)}`}>Review</a>
                        </li>
                      );
                    }
                    const r = t.row;
                    const wait = waitDays(r.since, instant);
                    // A reply waiting: the row IS the link — the review scrolled to that exercise, or (a thread whose
                    // workout was reset has no review) the client's conversation; `Reply` on the right says so (William 10-14).
                    const to = r.sessionId === null ? href({ name: "thread", clientId: r.clientId })
                      : `${href({ name: "review", sessionId: r.sessionId })}?exercise=${r.exerciseRowId ?? ""}`;
                    return (
                      <li key={r.commentId} className="queue-row is-linked">
                        <a className="queue-open" href={to} aria-label={`Reply to ${r.firstName} about ${r.exercise}`} />
                        <span className="queue-title">{fullName(r.clientId, r.firstName)}</span>
                        {/* JOB 3 · WILLIAM (10-15): the row's `Reply` on the right says what it is — the middle no longer says it
                            again (`Reply · Full Body A` beside `Reply`); `replySubject` stays the phone's (no shown action there). */}
                        <span className="queue-workout">{r.workout ?? r.exercise}</span>
                        <span className={isLongWait(wait) ? "queue-wait is-long" : "queue-wait"}>{waitShort(wait)}</span>
                        <span className="ht-button queue-review queue-go" aria-hidden="true">Reply</span>
                      </li>
                    );
                  })}
                </ol>}
                {showAllLabel(todo.length, showAll) !== null && (
                  <a className="queue-more" href={`${href({ name: "dashboard" })}?queue=all`}>{showAllLabel(todo.length, showAll)}</a>
                )}
            </section>

            <RemindersCard state={reminders.state} write={reminders.write} now={reminderNow} zone={zone} onEdit={editReminder} />
            <ReviewingCard today={dayKeyOf(today)} />
          </div>
        )}
        {/* The clients he REVIEWS (his question 2026-10-11), read-only — under To do in the stack, or on its own with no clients. */}
        {!(q !== null && clients > 0) && <ReviewingCard today={dayKeyOf(today)} />}
        {reminderTarget !== null && (
          <ReminderWindow target={reminderTarget} onClose={() => setReminderTarget(null)}
                          clients={reminders.state.status === "ready" ? reminders.state.clients : []}
                          write={reminders.write} zone={zone} now={reminderNow} />
        )}
        {callTarget !== null && (
          <CallWindow target={callTarget} onClose={() => setCallTarget(null)} write={calls.write} zone={zone} now={reminderNow} />
        )}
        {CALLS_INCLUDED && <CallHost onEnded={calls.write.ended} />}
      </main>
    </Workspace>
  );
}
